import type {
  ICellRendererComp,
  ICellRendererParams,
  ColDef,
} from "ag-grid-enterprise";

interface SelectConfig {
  value: number;
  options: Array<{ id: number; name: string }>;
  patch_url: string;
  patch_wrapper: string;
  patch_id: number;
  patch_value_key: string;
}

/**
 * StatusCellRenderer — generic presentation renderer.
 *
 * Handles three patterns, checked in order:
 *
 * 1. Select  — `field_select` key in row data (e.g. `role_select`)
 *    Renders an interactive <select>; on change PATCHes the configured endpoint.
 *    Config: { value, options, patch_url, patch_wrapper, patch_id, patch_value_key }
 *
 * 2. HTML    — `field_html` key in row data (e.g. `email_html`, `status_html`)
 *    Renders server-provided HTML with the `ag-status-cell` flex-column layout.
 *    Backwards-compatible: the existing `status` column uses `status_html` which
 *    resolves naturally via `field + "_html"` when field === "status".
 *
 * 3. Plain   — falls back to `params.value` as text.
 *
 * The `actionsCellRenderer` is the counterpart for all interactive button actions.
 */
export class StatusCellRenderer implements ICellRendererComp {
  private eGui!: HTMLElement;

  init(params: ICellRendererParams): void {
    this.eGui = document.createElement("div");

    if (params.node.group) {
      this.eGui.textContent = params.value ?? "";
      return;
    }

    const field = (params.colDef as ColDef)?.field ?? "";
    const data = params.data as Record<string, unknown>;

    // Pattern 1: Select
    const cfg = data[field + "_select"] as SelectConfig | undefined | null;
    if (cfg) {
      this.eGui.style.width = "100%";
      const select = document.createElement("select");
      select.style.width = "100%";
      select.style.maxWidth = "100%";

      cfg.options.forEach(({ id, name }) => {
        const opt = document.createElement("option");
        opt.value = String(id);
        opt.textContent = name;
        if (id === cfg.value) opt.selected = true;
        select.appendChild(opt);
      });

      select.addEventListener("change", async () => {
        const newValue = parseInt(select.value, 10);
        const token =
          (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)
            ?.content ?? "";
        try {
          const response = await fetch(cfg.patch_url, {
            method: "PATCH",
            headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
            body: JSON.stringify({
              [cfg.patch_wrapper]: [{ id: cfg.patch_id, [cfg.patch_value_key]: newValue }],
            }),
          });
          const json = (await response.json()) as { status: string };
          if (json.status === "ok") {
            // Keep the grid's underlying row data (sort/filter/export source) in sync with
            // the dropdown — otherwise it keeps sorting/filtering on the pre-edit role name.
            const newOption = cfg.options.find(({ id }) => id === newValue);
            if (newOption) {
              // Update cfg.value before setDataValue — the latter triggers a synchronous
              // cell refresh (refresh() always returns false), which reads cfg.value to
              // pick the selected <option> when it rebuilds the <select> from scratch.
              cfg.value = newValue;
              params.node.setDataValue(field, newOption.name);
            }
          } else {
            select.value = String(cfg.value);
          }
        } catch {
          select.value = String(cfg.value);
        }
      });

      this.eGui.appendChild(select);
      return;
    }

    // Pattern 2: HTML (ag-status-cell layout — only when HTML content present)
    const html = data[field + "_html"] as string | undefined;
    if (html) {
      this.eGui.className = "ag-status-cell";
      this.eGui.innerHTML = html.replace(/>\s+</g, "><").trim();
    } else {
      // Pattern 3: Plain text fallback
      this.eGui.textContent = params.value ?? "";
    }
  }

  getGui(): HTMLElement {
    return this.eGui;
  }

  refresh(): boolean {
    return false;
  }
}
